'use client'

import { useEffect, useRef } from 'react'

interface FaqTocSpyProps {
  sectionIds: string[]
}

/**
 * Wires an IntersectionObserver to the FAQ section headings and marks the
 * corresponding TOC link with `is-active` (brand text + tally dot visible).
 *
 * Renders nothing — purely a behaviour layer dropped into the page.
 */
export function FaqTocSpy({ sectionIds }: FaqTocSpyProps) {
  const activeRef = useRef<string | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const setActive = (id: string) => {
      if (activeRef.current === id) return
      activeRef.current = id

      // Remove is-active from all TOC links
      document.querySelectorAll('[data-toc-link]').forEach(el => {
        el.classList.remove('is-active')
      })

      // Apply is-active to the matching link
      const link = document.querySelector(`[data-toc-link="${id}"]`)
      link?.classList.add('is-active')
    }

    const observer = new IntersectionObserver(
      entries => {
        // Find the topmost entry that is intersecting
        const intersecting = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)

        if (intersecting.length > 0) {
          setActive(intersecting[0].target.id)
        }
      },
      {
        rootMargin: '-10% 0px -60% 0px',
        threshold: 0,
      }
    )

    sectionIds.forEach(id => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })

    // Activate first section immediately
    if (sectionIds[0]) setActive(sectionIds[0])

    return () => observer.disconnect()
  }, [sectionIds])

  return null
}
